import type { Theme } from '@mui/material';
import { Autocomplete, Box, Grid, TextField, Typography, useTheme } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';
import type { CompanyFormData } from '../../schemas/companyInfoSchema';

const textFieldStyles = (theme: Theme) => ({
  '& label': {
    color: theme.palette.text.primary,
    '&.Mui-focused': {
      color: theme.palette.text.primary,
      borderColor: theme.palette.bidlogiq?.orange,
    },
    '&.MuiInputLabel-shrink': {
      fontSize: '.9rem',
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.bidlogiq?.orange,
      borderWidth: '2px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.bidlogiq?.orange,
      borderWidth: '2px',
    },
  },
});

interface Props {
  data: Partial<CompanyFormData>;
  onChange: (data: Partial<CompanyFormData>) => void;
}

const NoCollateralCompanyInfo: React.FC<Props> = ({ data, onChange }) => {
  const theme = useTheme();

  const handleChange = (field: keyof CompanyFormData, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  const timezones = useMemo(() => {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Australia/Sydney',
      ];
    }
  }, []);

  return (
    <Box
      sx={{
        mt: 0,
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
        Tender-Specific Company Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Because External Client is enabled, global company info will be ignored. Please provide the
        company details specific to this tender below.
      </Typography>

      <Grid container direction="column" spacing={3}>
        <Grid>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Company Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={data.clientName || ''}
                onChange={(e) => handleChange('clientName', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Legal Name"
                value={data.company_legal_name || ''}
                onChange={(e) => handleChange('company_legal_name', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Primary business registration (ABN, etc.)"
                value={data.company_registration || ''}
                onChange={(e) => handleChange('company_registration', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Secondary business registration (ACN, etc.)"
                value={data.secondary_company_registration || ''}
                onChange={(e) => handleChange('secondary_company_registration', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Website"
                value={data.company_website || ''}
                onChange={(e) => handleChange('company_website', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Phone"
                value={data.company_phone || ''}
                onChange={(e) => handleChange('company_phone', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={timezones}
                value={data.timezone || null}
                onChange={(_, newValue) => handleChange('timezone', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Timezone"
                    fullWidth
                    sx={textFieldStyles(theme)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Company Address"
                multiline
                rows={4}
                value={data.company_address || ''}
                onChange={(e) => handleChange('company_address', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Primary Contact
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Name"
                value={data.company_contact_name || ''}
                onChange={(e) => handleChange('company_contact_name', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={data.company_contact_email || ''}
                onChange={(e) => handleChange('company_contact_email', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Role"
                value={data.company_contact_role || ''}
                onChange={(e) => handleChange('company_contact_role', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Facsimile"
                value={data.contact_facsimile || ''}
                onChange={(e) => handleChange('contact_facsimile', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Insurance Details
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
            Public Liability Insurance
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer"
                value={data.liability_insurer || ''}
                onChange={(e) => handleChange('liability_insurer', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={data.liability_insurance_expiry || ''}
                onChange={(e) => handleChange('liability_insurance_expiry', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Limit per Claim"
                value={data.liability_insurance_per_claim || ''}
                onChange={(e) => handleChange('liability_insurance_per_claim', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Aggregate Limit"
                value={data.liability_insurance_aggregate || ''}
                onChange={(e) => handleChange('liability_insurance_aggregate', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'medium' }}>
            Professional Indemnity Insurance
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer"
                value={data.indemnity_insurer || ''}
                onChange={(e) => handleChange('indemnity_insurer', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={data.indemnity_insurer_expiry || ''}
                onChange={(e) => handleChange('indemnity_insurer_expiry', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Limit per Claim"
                value={data.indemnity_insurer_per_claim || ''}
                onChange={(e) => handleChange('indemnity_insurer_per_claim', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Aggregate Limit"
                value={data.indemnity_insurer_aggregate || ''}
                onChange={(e) => handleChange('indemnity_insurer_aggregate', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'medium' }}>
            Workers Compensation
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer 1"
                value={data.workers_compensation_insurer_1 || ''}
                onChange={(e) => handleChange('workers_compensation_insurer_1', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date 1"
                type="date"
                value={data.workers_compensation_expiry_1 || ''}
                onChange={(e) => handleChange('workers_compensation_expiry_1', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer 2"
                value={data.workers_compensation_insurer_2 || ''}
                onChange={(e) => handleChange('workers_compensation_insurer_2', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date 2"
                type="date"
                value={data.workers_compensation_expiry_2 || ''}
                onChange={(e) => handleChange('workers_compensation_expiry_2', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer 3"
                value={data.workers_compensation_insurer_3 || ''}
                onChange={(e) => handleChange('workers_compensation_insurer_3', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date 3"
                type="date"
                value={data.workers_compensation_expiry_3 || ''}
                onChange={(e) => handleChange('workers_compensation_expiry_3', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Insurer 4"
                value={data.workers_compensation_insurer_4 || ''}
                onChange={(e) => handleChange('workers_compensation_insurer_4', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Expiry Date 4"
                type="date"
                value={data.workers_compensation_expiry_4 || ''}
                onChange={(e) => handleChange('workers_compensation_expiry_4', e.target.value)}
                sx={textFieldStyles(theme)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NoCollateralCompanyInfo;
